/* eslint-disable linebreak-style */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable no-param-reassign */

import { compact } from 'underscore';
import { ChatCompletionRequestMessageRoleEnum } from 'openai';
import { ChatRole, useTextCompletion, ChatMessage as ChatMessageDto } from '../Repository/TextCompletion';
import { LLMModel, PromptingMethod } from '../Components/Contexts/LLMSettingContext';
import { useLLMSetting } from '../Components/Contexts';

export const botName = process.env.REACT_APP_BOT_NAME;

const tryExtractAnswer = (result: string) => {
  try {
    const obj = JSON.parse(result);

    if (obj?.answer) {
      return obj.answer;
    }

    return result;
  } catch {
    return result;
  }
};

declare interface Message {
  content: string;
  sender: string;
}

function createMessage({
  content = '',
  mine = true,
}) {
  return {
    content,
    sender: mine ? 'user' : botName,
  } as Message;
}

const history: Message[] = [];

export const useBot = () => {
  const {
    temperature,
    maxTokens,
    chatPrompt,
    promotePrompt,
    evalPrompt,
    prompt,
    prompts,
    model,
    deployment,
    promptingMethod,
    ignoreDefaultFewShotExamples,
    conversationId,
    autoSave,
  } = useLLMSetting();

  let messages = history;
  const { getTextCompletion, getChatCompletion } = useTextCompletion();

  const sendMessage = async (content: string) => {
    content = content.replace(/\n/g, '<br>');
    const msg = createMessage({ content });
    const newMessages = messages.concat(msg);
    messages = newMessages;

    const messageParam = newMessages.map((msg) => ({
      role: (msg as Message).sender === botName ? 'assistant' : 'user' as ChatCompletionRequestMessageRoleEnum,
      content: (msg as Message).content || '',
    }));

    let result;

    if (promptingMethod === PromptingMethod.Default && model === LLMModel.Davinci003) {
      const composedPrompt = `${chatPrompt}
${messageParam.map((msg) => `${msg.role === 'assistant' ? '¬AI¬' : '¬Human¬'}\n${msg.content}`).reduce((prev, curr) => `${prev}\n${curr}`)}
¬AI¬`;
      result = await getTextCompletion(composedPrompt, maxTokens, temperature);
    } else if (promptingMethod === PromptingMethod.SeparatePrompts) {
      const [
        evaluation,
        promotion,
        chat,
      ] = await Promise.all([
        // Evaluate
        getChatCompletion('', [{
          Role: ChatRole.User,
          Content: evalPrompt.replace('%s', `${messageParam.map((msg) => `${msg.role === 'assistant' ? 'assistant:' : 'user:'} ${tryExtractAnswer(msg.content)}`).reduce((prev, curr) => `${prev}\n${curr}`)}`),
        }], maxTokens, 0 /* Hard coded to 0 for a more stable result */, true, model, deployment, conversationId, autoSave).then((res) => {
          try {
            const obj = JSON.parse(res || '');

            if (obj.score && obj.explanation) {
              const score = Number.parseInt(obj.score, 10);

              if (Number.isInteger(score)) {
                return {
                  score: obj.score,
                  explanation: obj.explanation,
                };
              }
            }

            throw new Error();
          } catch {
            return {
              score: 0,
              explanation: 'Failed to parse evaluation response',
            };
          }
        }),
        // Promote
        getChatCompletion(
          '',
          [...messageParam.map((msg) => ({
            Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
            Content: msg.content,
          } as ChatMessageDto)), {
            Role: ChatRole.User,
            Content: promotePrompt,

          }],
          maxTokens,
          temperature,
          true,
          model,
          deployment,
          conversationId,
          autoSave,
        ).then((res) => {
          try {
            const obj = JSON.parse(res || '');

            if (obj?.answer && obj?.query) {
              return {
                anwser: obj.answer,
                query: obj.query,
              };
            }

            throw new Error();
          } catch {
            return null;
          }
        }),
        // Chat
        getChatCompletion('', [...messageParam.map((msg) => ({
          Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
          Content: tryExtractAnswer(msg.content),
        } as ChatMessageDto)), {
          Role: ChatRole.User,
          Content: chatPrompt,
        }], maxTokens, temperature, true, model, deployment, conversationId, autoSave),
      ]);

      const shouldPromote = evaluation?.score && evaluation.score >= 7;

      result = JSON.stringify({
        mode: shouldPromote ? 'Promote' : 'Chat',
        answer: shouldPromote ? promotion?.anwser : chat,
        score: evaluation?.score,
        query: shouldPromote ? promotion?.query : '',
        evaluation: evaluation?.explanation,
      });
    } else if (promptingMethod === PromptingMethod.Default) {
      const systemMessages = prompt ? [{
        Role: ChatRole.System,
        Content: prompt,
      } as ChatMessageDto] : [];
      result = await getChatCompletion(
        '',
        systemMessages.concat(messageParam.map((msg) => ({
          Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
          Content: msg.content,
        } as ChatMessageDto))),
        maxTokens,
        temperature,
        ignoreDefaultFewShotExamples,
        model,
        deployment,
        conversationId,
        autoSave,
      );
    } else if (promptingMethod === PromptingMethod.MultipleSystemPrompt) {
      const filteredPrompts = compact(prompts);
      const systemMessages = filteredPrompts.map((prompt) => ({
        Role: ChatRole.System,
        Content: prompt,
      } as ChatMessageDto));
      result = await getChatCompletion(
        '',
        systemMessages.concat(messageParam.map((msg) => ({
          Role: msg.role === 'assistant' ? ChatRole.Assistant : ChatRole.User,
          Content: msg.content,
        } as ChatMessageDto))),
        maxTokens,
        temperature,
        ignoreDefaultFewShotExamples,
        model,
        deployment,
        conversationId,
        autoSave,
      );
    }

    // TODO, define some rules like format checking

    const formattedResContent = (result ?? '').replace(/^(\n|<br>)+|(\n|<br>)+$/g, '').replace(/[\n]+/g, '<br><br>');

    messages = newMessages.concat([
      createMessage({ content: formattedResContent, mine: false }),
    ]);

    return formattedResContent;
  };

  const clearHistory = () => {
    messages = history;
  };

  return {
    sendMessage,
    clearHistory,
  };
};
